import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, cn } from "@nexus/ui";

import type { MarketplaceOrder, MarketplaceOrderStatus } from "../types/marketplace.types";
import {
  ORDER_HANDOFF_SLOW_THRESHOLD_MS,
  ORDER_HANDOFF_SUCCESS_VISIBLE_MS,
  elapsedMsSince,
  isInFlightHandoffStatus,
} from "../lib/order-handoff-ux";

type HandoffAlertStatus = "FAILED" | "PENDING_SEND" | "SENDING" | "AWAITING_VENDOR";

function handoffAlertStatus(status: MarketplaceOrderStatus): HandoffAlertStatus | null {
  if (
    status === "FAILED" ||
    status === "PENDING_SEND" ||
    status === "SENDING" ||
    status === "AWAITING_VENDOR"
  ) {
    return status;
  }
  return null;
}

function canRetryHandoff(status: MarketplaceOrderStatus): boolean {
  return (
    status === "FAILED" ||
    status === "PENDING_SEND" ||
    status === "SENDING" ||
    status === "AWAITING_VENDOR"
  );
}

interface OrderHandoffAlertProps {
  order: MarketplaceOrder;
  retryPending: boolean;
  onRetry: () => void;
}

export function OrderHandoffAlert({ order, retryPending, onRetry }: OrderHandoffAlertProps) {
  const { t } = useTranslation();
  const [elapsedMs, setElapsedMs] = useState(() => elapsedMsSince(order.createdAt));
  const [showSuccess, setShowSuccess] = useState(false);
  const prevStatusRef = useRef(order.status);

  useEffect(() => {
    if (!isInFlightHandoffStatus(order.status)) {
      return;
    }

    const tick = () => setElapsedMs(elapsedMsSince(order.createdAt));
    tick();
    const intervalId = window.setInterval(tick, 1_000);
    return () => window.clearInterval(intervalId);
  }, [order.createdAt, order.status]);

  useEffect(() => {
    const previousStatus = prevStatusRef.current;
    if (isInFlightHandoffStatus(previousStatus) && order.status === "SENT") {
      setShowSuccess(true);
      const timerId = window.setTimeout(
        () => setShowSuccess(false),
        ORDER_HANDOFF_SUCCESS_VISIBLE_MS,
      );
      prevStatusRef.current = order.status;
      return () => window.clearTimeout(timerId);
    }

    prevStatusRef.current = order.status;
  }, [order.status]);

  const handoffAlert = handoffAlertStatus(order.status);

  if (!handoffAlert && !showSuccess) {
    return null;
  }

  if (showSuccess) {
    return (
      <div
        role="status"
        className="mb-3 rounded-lg border border-success/30 bg-[color-mix(in_oklch,var(--success)_10%,transparent)] px-3 py-2.5"
      >
        <p className="text-sm font-medium text-foreground">
          {t("marketplace.orders.handoff.success.title", "Sent to production partner")}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t(
            "marketplace.orders.handoff.success.description",
            "Your scripts were delivered to the production partner successfully.",
          )}
        </p>
      </div>
    );
  }

  if (!handoffAlert) {
    return null;
  }

  const showSlowWarning =
    !isInFlightHandoffStatus(order.status) ||
    elapsedMs >= ORDER_HANDOFF_SLOW_THRESHOLD_MS;

  if (!showSlowWarning) {
    return (
      <div
        role="status"
        className="mb-3 flex items-start gap-3 rounded-lg border border-border/60 bg-muted/30 px-3 py-3"
      >
        <Icon
          name="loader"
          size="sm"
          className="mt-0.5 shrink-0 animate-spin text-muted-foreground"
          aria-hidden
        />
        <div>
          <p className="text-sm font-medium text-foreground">
            {t(`marketplace.orders.handoff.${handoffAlert}.title`)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t(
              "marketplace.orders.handoff.sendingProgress",
              "We're sending your order to the partner. This usually completes within a minute.",
            )}
          </p>
        </div>
      </div>
    );
  }

  const retryable = canRetryHandoff(order.status);

  return (
    <div
      className={cn(
        "mb-3 rounded-lg border px-3 py-2.5",
        handoffAlert === "FAILED"
          ? "border-destructive/30 bg-destructive/5"
          : "border-status-warning/30 bg-status-warning-subtle/40",
      )}
      role="status"
    >
      <p
        className={cn(
          "text-sm font-medium",
          handoffAlert === "FAILED" ? "text-destructive" : "text-foreground",
        )}
      >
        {t(`marketplace.orders.handoff.${handoffAlert}.title`)}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {t(`marketplace.orders.handoff.${handoffAlert}.description`)}
      </p>
      {retryable ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={retryPending}
            onClick={(event) => {
              event.stopPropagation();
              onRetry();
            }}
          >
            {retryPending
              ? t("marketplace.orders.handoff.retrying")
              : t("marketplace.orders.handoff.retry")}
          </Button>
          <span className="text-xs text-muted-foreground">
            {t("marketplace.orders.handoff.supportHint")}
          </span>
        </div>
      ) : null}
    </div>
  );
}
